import {
  Alert,
  Box,
  Button,
  Card,
  CardActionArea,
  CardActions,
  CardContent,
  Link,
  Stack,
  Typography,
} from "@mui/material";
import { useEffect, useRef } from "react";
import { ListPortraitsResponse } from "tng-address-collection-shared/types/portraitsApiTypes.ts";
import { usePortraitsTranslator } from "../i18n/translator";
import { useFlagInGallery } from "./useAdminPortraits";

const THUMBNAIL_SIZE = 112;

const Thumbnail = ({ url, alt }: { url: string; alt: string }) => (
  <Box
    component="img"
    src={url}
    alt={alt}
    loading="lazy"
    width={THUMBNAIL_SIZE}
    height={THUMBNAIL_SIZE}
    bgcolor="grey.100"
    borderRadius={1}
    sx={{ objectFit: "contain" }}
  />
);

export const GalleryTile = ({
  employee: { uidNumber, name, portrait },
  selected,
}: {
  employee: ListPortraitsResponse["employees"][number];
  selected: boolean;
}) => {
  const translator = usePortraitsTranslator();
  const flag = useFlagInGallery();
  const tileRef = useRef<HTMLDivElement>(null);

  // Previous and next in the panel move the selection out of sight
  useEffect(() => {
    if (!selected) {
      return;
    }
    const reducedMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    ).matches;
    tileRef.current?.scrollIntoView({
      behavior: reducedMotion ? "auto" : "smooth",
      block: "nearest",
    });
  }, [selected]);
  const choice = portrait?.choice;

  return (
    <Card
      ref={tileRef}
      variant="outlined"
      aria-current={selected || undefined}
      sx={{
        borderColor: selected
          ? "primary.main"
          : portrait?.flagged
            ? "warning.main"
            : undefined,
        borderWidth: selected ? 2 : undefined,
        display: "flex",
        flexDirection: "column",
      }}
    >
      <CardActionArea
        component={Link}
        href={`/admin/${uidNumber}`}
        sx={{ flexGrow: 1 }}
      >
        <CardContent>
          <Typography fontWeight="bold" gutterBottom>
            {name}
          </Typography>
          <Stack direction="row" spacing={1} alignItems="center">
            {portrait?.cutoutUrl ? (
              <Thumbnail
                url={portrait.cutoutUrl}
                alt={`${translator("photoOf")} ${name}`}
              />
            ) : (
              <Typography color="text.secondary">
                {translator("noPortrait")}
              </Typography>
            )}
            {choice?.kind === "christmasImage" ? (
              <Thumbnail
                url={choice.imageUrl}
                alt={`${translator("christmasImageOf")} ${name}`}
              />
            ) : (
              portrait && (
                <Typography color="text.secondary">
                  {translator(
                    choice?.kind === "photoOnly"
                      ? "photoOnlyShort"
                      : "notChosenYet",
                  )}
                </Typography>
              )
            )}
          </Stack>
        </CardContent>
      </CardActionArea>
      {portrait?.cutoutUrl && (
        <CardActions>
          <Button
            color="warning"
            aria-pressed={portrait.flagged}
            disabled={flag.isPending}
            onClick={() =>
              flag.mutate({ uidNumber, flagged: !portrait.flagged })
            }
          >
            {translator(portrait.flagged ? "unflag" : "flag")}
          </Button>
        </CardActions>
      )}
      {flag.isError && (
        <Alert severity="error" sx={{ mx: 1, mb: 1 }}>
          {translator("unknownErrorTryAgainLater")}
        </Alert>
      )}
    </Card>
  );
};
